import { cn } from '@yeshuman/sdk'
import type { ReactNode } from 'react'
import { Pencil, Plus } from 'lucide-react'

import { SkillNameComposer } from './skill-name-composer'

export const skillManagerAddRowClassName = 'rounded-md border border-dashed px-3 py-2'
export const skillManagerSkillRowClassName = 'rounded-md border px-3 py-2'

const skillManagerEditableNameButtonClassName =
  'group/name flex min-w-0 items-center gap-1.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'

const skillManagerHoverPencilClassName =
  'size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/name:opacity-100 group-focus-visible/name:opacity-100'

export function SkillManagerEditableNameButton({
  name,
  disabled,
  onStartEdit,
  className,
}: {
  name: string
  disabled?: boolean
  onStartEdit: () => void
  /** Pass `flex-1` when the name sits in a full-width row slot (job skills). */
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(skillManagerEditableNameButtonClassName, className)}
      onClick={onStartEdit}
      disabled={disabled}
    >
      <span className="truncate text-sm font-medium leading-snug text-foreground">{name}</span>
      <Pencil className={skillManagerHoverPencilClassName} aria-hidden />
    </button>
  )
}

/** Metrics / toggle cluster + `ml-6` actions slot — matches profile sources → menu rhythm. */
export function SkillManagerTrailingCluster({
  leading,
  actions,
}: {
  leading: ReactNode
  actions: ReactNode
}) {
  return (
    <div className="flex shrink-0 items-center">
      {leading}
      <div className="ml-6 flex items-center">{actions}</div>
    </div>
  )
}

export function SkillManagerAddRow({
  draft,
  error,
  disabled,
  autoFocus,
  inputId,
  placeholder = 'Add skill…',
  excludeNames,
  suggestionItems,
  suggestionsLoading,
  onDraftChange,
  onAdd,
}: {
  draft: string
  error: string | null
  disabled?: boolean
  autoFocus?: boolean
  inputId?: string
  placeholder?: string
  excludeNames: string[]
  suggestionItems?: { name: string }[]
  suggestionsLoading?: boolean
  onDraftChange: (next: string) => void
  onAdd: (name: string) => void
}) {
  return (
    <li className={skillManagerAddRowClassName}>
      <div className="flex min-h-8 items-center gap-2">
        <Plus className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <SkillNameComposer
          id={inputId}
          value={draft}
          onValueChange={onDraftChange}
          excludeNames={excludeNames}
          disabled={disabled}
          placeholder={placeholder}
          autoFocus={autoFocus}
          suggestionItems={suggestionItems}
          suggestionsLoading={suggestionsLoading}
          onCommit={onAdd}
        />
      </div>
      {error ? (
        <p className="mt-1 pl-6 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  )
}

export function SkillManagerEditComposer({
  value,
  onValueChange,
  excludeNames,
  disabled,
  editError,
  editHint,
  suggestionItems,
  suggestionsLoading,
  enableSuggestions = false,
  onCommit,
  onCancel,
}: {
  value: string
  onValueChange: (next: string) => void
  excludeNames: string[]
  disabled?: boolean
  editError?: string | null
  editHint?: string | null
  suggestionItems?: { name: string }[]
  suggestionsLoading?: boolean
  enableSuggestions?: boolean
  onCommit: (name: string) => void
  onCancel?: () => void
}) {
  return (
    <div className="min-w-0 flex-1 space-y-1">
      <SkillNameComposer
        value={value}
        onValueChange={onValueChange}
        excludeNames={excludeNames}
        disabled={disabled}
        placeholder="Skill name"
        autoFocus
        enableSuggestions={enableSuggestions}
        suggestionItems={suggestionItems}
        suggestionsLoading={suggestionsLoading}
        commitOnBlur
        onCommit={onCommit}
        onCancel={onCancel}
      />
      {editHint ? <p className="text-xs text-muted-foreground">{editHint}</p> : null}
      {editError ? (
        <p className="text-sm text-destructive" role="alert">
          {editError}
        </p>
      ) : null}
    </div>
  )
}
